<template>
  <div class="mt-1 mb-3">
    <div class="row align-items-center">
      <div class="col-7">
        <div v-show="editMode && task.type">
          <label for="title" hidden>Task Title</label>
          <textarea
            id="title"
            class="form-control task-title-input"
            rows="1"
            ref="task-title-input"
            v-model="title"
            :style="titleInputFieldStyle"
            :placeholder="placeholder"
            @keydown.esc="cancelEditing"
            @keydown.enter="saveChanges"
          />
        </div>
        <div v-show="!editMode || !task.type">
          <div
            class="d-flex align-self-center task-title"
            @click="triggerEditMode"
            :class="{ 'text-muted': !title }"
          >
            {{ title || placeholder }}
          </div>
        </div>
      </div>

      <div class="col-5 text-right">
        <div class="d-flex justify-content-end align-items-center">
          <button
              class="btn btn-link no-outline shadow-none"
              style="padding: 0 !important; margin: 0 auto 0 0"
              v-if="hasDetails"
              @click="triggerDisplayDetails"
          >
            <i class="fas" :class="dropDownIcon"></i>
          </button>
          <button
            class="btn btn-link no-outline shadow-none"
            style="padding: 0 !important; margin: 0 auto 0 0"
            v-if="hasSubtasks && !isExternalMember"
            @click="triggerDisplaySubtask"
          >
            <i class="fas" :class="subtaskDropdownIcon"></i>
          </button>

          <div
            class="task-completed-at text-muted mr-2"
            v-if="isCompleted || isStoppedPlayable"
          >
            {{ formattedCompletedAt }}
          </div>

          <div v-if="isPendingApproval">
            <div v-if="isExternalMember">
              <Checkbox
                size="small"
                type="secondary"
                :disabled="true"
                :isPauseIcon="true"
              />
            </div>

            <div class="d-flex" v-else>
              <Checkbox
                size="small"
                type="danger"
                @clicked="rejectTask"
                :isCrossIcon="true"
              />
              <Checkbox size="small" type="success" @clicked="approveTask" />
            </div>
          </div>

          <Checkbox
            size="small"
            :type="isExternalMember ? 'danger' : 'secondary'"
            :disabled="true"
            :isCrossIcon="true"
            v-if="isRejected"
          />

          <Checkbox
            size="small"
            :type="checkboxType"
            :disabled="isHourlyTask"
            @clicked="saveTaskCompletion"
            v-if="!isRejected && !isPendingApproval"
          />
        </div>
      </div>
    </div>

    <div
      class="row"
      v-if="
        (!!taskUsername && !isPersonal) ||
        !!taskLabelsText ||
        (isPersonallyCreated && toPickUp)
      "
    >
      <div class="col">
        <div class="d-flex align-items-center" style="padding: 0 12px">
          <span
            class="text-muted text-grey task-details"
            v-if="taskUsername"
            v-text="taskUsername"
          ></span>

          <span
            class="text-muted text-grey task-details font-weight-bold"
            v-if="taskUsername && teamLabelText"
          >
            -
          </span>

          <span
            class="text-muted text-grey task-details font-weight-bold"
            v-if="taskLabelsText"
            v-text="taskLabelsText"
          ></span>

          <span
            class="text-muted text-grey task-details font-weight-bold"
            v-if="taskLabelsText && teamLabelText"
          >
            -
          </span>

          <span
            class="text-muted text-grey task-details"
            v-text="teamLabelText"
            v-if="isAssignedToTeam"
          ></span>

          <button
            type="button"
            class="btn wfh-link mx-2"
            style="padding: 0 !important; font-size: 12px"
            v-if="toPickUp"
            @click="pickUpTask"
          >
            Pick Up
          </button>
        </div>
      </div>
    </div>

    <div class="row align-items-center" v-if="editMode">
      <div class="col">
        <button type="button" class="advanced-button" @click="editAdvanceTask">
          Advanced
        </button>
      </div>

      <div class="col text-right">
        <button class="border-0 bg-transparent no-outline p-0">
          <img
            src="/images/tag.svg"
            style="width: 24px; height: 24px"
            @click="updatingLabels = true"
          />
        </button>

        <button
          class="border-0 bg-transparent no-outline p-0"
          v-if="!isHourlyTask"
          @click="updatingAssignees = true"
        >
          <img src="/images/person-add.svg" />
        </button>

        <button
          class="border-0 bg-transparent no-outline p-0"
          v-if="validTask"
          @click="promptTaskDeletion = true"
        >
          <img src="/images/delete.svg" />
        </button>
      </div>
    </div>

    <div v-if="hasSubtasks && displaySubtask">
      <div class="row pt-2">
        <div class="col">
          <div
            class="row mt-2"
            v-for="subtask in task.subtasks"
            :key="subtask.id"
          >
            <div class="col-10">
              <div class="d-flex flex-row align-items-center">
                <div>
                  <img src="/images/sub_drag.svg" alt="" />
                </div>
                <div class="subtask-title">
                  {{ subtask.title }}
                </div>
              </div>
            </div>
            <div class="col-auto ml-2 p-0">
              <Checkbox
                :disabled="viewOnly"
                :type="subtask.completed_at ? 'primary' : ''"
                @clicked="saveTaskCompletion($event, subtask)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-show="showDetails">
      <div class="row mb-2 text-muted" v-for="playable of stoppedPlayable">
        <div class="col d-flex align-items-center">
          <div>
            <img src="/images/sub_drag.svg" alt=""/>
          </div>
          <div class="ml-2 playable-details">
            {{ formatDate(playable.stopped_at) }} ({{ getDifference(playable.started_at, playable.stopped_at) }})
          </div>
        </div>
      </div>
    </div>

    <task-modal-remove
      v-if="promptTaskDeletion"
      @clickDelete="deleteTask"
      @close="promptTaskDeletion = false"
    />

    <staff-selection
      v-if="updatingAssignees"
      :existingMembers="existingAssignees"
      @closeModal="updatingAssignees = false"
      @receiveStaffs="assigneesUpdate"
    ></staff-selection>

    <add-label-modal
      v-if="updatingLabels && !!labelOptions"
      :required="false"
      :options="labelOptions"
      :selected="selectedLabelName"
      @selectItem="saveTaskLabel"
      @deleteItem="deleteLabel"
      @updateLabel="updateLabel"
      @closeModal="updatingLabels = false"
    ></add-label-modal>

    <transition name="fade">
      <OpenTaskDetailsModal
        v-if="viewDetails"
        :task="task"
        :actionText="task.completed_at ? 'Mark as not done' : 'Mark As Done'"
        :editable="false"
        :hasCTA="false"
        @close="viewDetails = !viewDetails"
      />
    </transition>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import {
  destroy,
  store,
  update,
  updateSubtask,
  pickupTask,
} from '../../../../services/taskService'
import Checkbox from '../../../../atoms/Tasks/Checkbox'
import OpenTaskDetailsModal from '../../../Misc/Modal/OpenTaskDetailsModal'
import Swal from 'sweetalert2'

export default {
  components: { OpenTaskDetailsModal, Checkbox },
  props: {
    placeholder: {
      type: String,
    },
    task: {
      type: Object,
      required: true,
    },
    isPersonal: {
      type: Boolean,
      default: false,
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
    labelOptions: {
      type: Array,
      required: false,
    },
    type: {
      type: String,
      default: 'task',
    },
    isCollaborator: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editMode: false,
      originalTitle: this.task?.title ?? null,
      title: this.task?.title ?? null,
      titleInputFieldHeight: 'auto',
      displaySubtask: false,
      promptTaskDeletion: false,
      updatingLabels: false,
      selectedLabel:
        this.task.labels && this.task.labels.length > 0
          ? this.task.labels[0]
          : null,
      temporaryLabel: null,
      updatingAssignees: false,
      existingAssignees: new Map(),
      viewDetails: false,
      showDetails:false,
      playable: [],
    }
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser
    },

    titleInputFieldStyle() {
      return {
        height: this.titleInputFieldHeight,
      }
    },

    timezone() {
      return this.$store.state.timezone
    },

    stoppedPlayable () {
      return this.playable?.filter(playable => playable.stopped_at !== null);
    },

    hasDetails () {
      return this.stoppedPlayable?.length > 0;
    },

    dropDownIcon () {
      return this.showDetails ? 'fa-angle-up' : 'fa-angle-down';
    },

    formattedCompletedAt() {
      const date =
        this.task.type === 'playable'
          ? this.task.playable_completed
          : this.task.completed_at

      return this.timezone
        ? moment.utc(date).tz(this.timezone).calendar()
        : moment.utc(date).local().calendar()
    },

    validTask() {
      return !!this.task?.id ?? false
    },

    isCompleted() {
      return this.validTask && !!this.task.completed_at
    },

    isStoppedPlayable() {
      return (
        this.validTask &&
        this.task.type === 'playable' &&
        !!this.task.playable_completed
      )
    },

    isHourlyTask() {
      return this.task.type === 'hourly'
    },

    subtaskDropdownIcon() {
      return this.displaySubtask ? 'fa-angle-up' : 'fa-angle-down'
    },

    hasSubtasks() {
      return this.task?.subtasks?.length > 0 ?? false
    },

    taskUsername() {
      if (this.isPersonal && !this.isAssignedToTeam) {
        return ''
      }

      let names = ''
      this.task.users.forEach((user) => {
        names += `${user.name}, `
      })

      return names.length > 0 ? names.substr(0, names.length - 2) : ''
    },

    taskLabelsText() {
      if (!this.task.type) {
        let taskLabels = ''

        for (const label of this.task.labels) {
          taskLabels += `${label.name}, `
        }

        return `${taskLabels.substr(0, taskLabels.length - 2)} - Metric`
      } else {
        if (!this.task.labels) return ''

        let taskLabels = ''

        for (const label of this.task.labels) {
          taskLabels += `${label.name}, `
        }

        return taskLabels.substr(0, taskLabels.length - 2)
      }
    },

    selectedLabelName() {
      return this.selectedLabel ? this.selectedLabel.name : ''
    },

    isAssignedToTeam() {
      return this.task.team
    },

    teamLabelText() {
      if (!this.task.team) {
        return ''
      }

      return `Assigned to team ${this.task.team.name}`
    },

    toPickUp() {
      return this.task.team && this.task.users.length === 0
    },

    isPersonallyCreated() {
      return this.task.created_by === this.currentUser.id
    },

    isPendingApproval() {
      return (
        this.task.requires_approval &&
        this.task.approved_at === null &&
        this.task.rejected_at === null
      )
    },

    isRejected() {
      return this.task.rejected_at !== null
    },

    checkboxType() {
      return this.isCompleted || this.isStoppedPlayable ? "primary" : ""
    },

    isExternalMember() {
      return this.$store.state.isExternal
    },

    isAdmin() {
      return this.$store.state.isAdmin
    },
  },

  watch: {
    title() {
      this.resizeTitleInputField()
    },

    task() {
      this.originalTitle = this.task?.title ?? null
      this.title = this.task?.title ?? null
      this.selectedLabel =
        this.task.labels && this.task.labels.length > 0
          ? this.task.labels[0]
          : null
      if (this.task.users && this.task.users.length > 0) {
        this.task.users.forEach((u) => this.existingAssignees.set(u.id, u))
      }
    },
  },

  methods: {
    triggerEditMode() {
      if (this.isExternalMember) {
        this.viewDetails = !this.viewDetails
        return
      }

      if (
        this.isAdmin ||
        ((this.isPersonal ||
          this.task.created_by === this.currentUser.id ||
          this.task.user_id === this.currentUser.id ||
          this.isCollaborator) &&
          !this.viewOnly &&
          (!this.toPickUp || this.isPersonallyCreated || this.isCollaborator))
      ) {
        this.editMode = !this.editMode

        if (this.editMode) {
          this.$nextTick(() => {
            this.$refs['task-title-input'].focus()
            this.resizeTitleInputField()
          })
        }
      }
    },

    triggerDisplaySubtask() {
      this.displaySubtask = !this.displaySubtask
    },

    triggerDisplayDetails () {
      this.showDetails = !this.showDetails;
    },

    cancelEditing() {
      this.title = this.originalTitle
      this.editMode = false

      this.$nextTick(() => {
        this.$refs['task-title-input'].blur()
      })
    },

    resizeTitleInputField() {
      this.titleInputFieldHeight = 'auto'

      this.$nextTick(() => {
        this.titleInputFieldHeight = `${this.$refs['task-title-input'].scrollHeight}px`
      })
    },

    async saveChanges(event) {
      event.preventDefault()

      if (this.originalTitle !== this.title) {
        let response = {}
        this.task.title = this.title

        if (this.task.id) {
          response = await update(this.task, { title: this.task.title })
        } else {
          response = await store(this.task, {
            assignees: [this.currentUser.id],
            label_id: this.temporaryLabel,
          })
        }

        this.$emit('taskUpdated', response.data.data)
      }

      this.triggerEditMode()
    },

    async saveTaskCompletion(event, subtask = null) {
      event.preventDefault()
      if (
        this.isAdmin ||
        this.isPersonal ||
        (!this.viewOnly && !this.toPickUp)
      ) {
        const task = subtask ?? this.task

        if (task.id) {
          let response = null

          if (this.type === 'task') {
            const isSubtask = !!task.task_id

            if (isSubtask) {
              response = await updateSubtask(task, {
                is_completed: !task.completed_at,
              })
            } else {
              response = await update(
                task,
                { is_completed: !task.completed_at },
                isSubtask
              )
            }
          } else {
            const completion = this.task.completed_at
              ? 'incomplete'
              : 'complete'
            response = await axios.post(
              `/companies/${this.currentUser.company.id}/goals/${this.task.id}/${completion}`
            )
          }

          this.$emit('taskUpdated', response.data.data)
        }
      }
    },

    async deleteTask() {
      if (this.task.id) {
        if (this.task.type) {
          await destroy(this.task)
        } else {
          const response = await axios.delete(
            `/companies/${this.currentUser.company.id}/goals/${this.task.id}`
          )

          this.$emit('taskUpdated', response.data.data)
        }

        this.$emit('taskDeleted', this.task)
        this.triggerEditMode()
      }
    },

    editAdvanceTask() {
      const params = {
        title: this.title,
        type: this.task.type ?? 'metric',
        completed_at: this.task.completed_at,
      }

      if (this.task.id) {
        params.id = this.task.id
      }

      const queryString = Object.keys(params)
        .map((key) => {
          if (params[key]) {
            return `${key}=${encodeURIComponent(params[key])}`
          }

          return ''
        })
        .join('&')

      window.location.href = `/tasks/advanced?${queryString}`
    },

    async deleteLabel(labelId) {
      this.$emit('deleteLabel', labelId)
    },

    async updateLabel(val, labelId) {
      let label = null

      if (labelId) {
        const res = await axios.patch(
          `/company/${this.currentUser.company.id}/labels/${labelId}`,
          { label: val }
        )
        label = res.data.data
      } else {
        const res = await axios.post(
          `/company/${this.currentUser.company.id}/labels`,
          { label: val }
        )
        label = res.data.data
      }

      this.$emit('saveLabel', { val, label })
    },

    async saveTaskLabel(value, labelId) {
      if (this.task.id) {
        if (this.task.type) {
          const response = await update(this.task, { label_id: labelId })

          this.$emit('taskUpdated', response.data.data)
        } else {
          const response = await axios.put(
            `/companies/${this.currentUser.company.id}/goals/${this.task.id}`,
            {
              label: value,
            }
          )

          this.$emit('taskUpdated', response.data.data)
        }
      } else {
        this.temporaryLabel = labelId
      }
    },

    async assigneesUpdate(assignees) {
      if (assignees.size === 0) {
        return
      }

      if (this.task.id) {
        this.existingAssignees = assignees

        await axios.put(`/tasks/${this.task.id}`, {
          assignees: [...assignees.keys()],
        })

        //refresh users data
        const res = await axios.get(`/tasks/${this.task.id}`)
        const taskData = res.data.data

        taskData.users.forEach((item) => {
          this.existingAssignees.set(item.id, item)
        })

        this.$emit('taskUpdated', taskData)

        Swal.fire({
          icon: 'success',
          title: 'Updated assignees',
        })
      }
    },

    async pickUpTask() {
      const res = await pickupTask(this.task)
      this.task = res.data.data
    },

    async approveTask() {
      const res = await axios.post(`/tasks/${this.task.id}/approve`)
      this.task = res.data.data
    },

    async rejectTask() {
      const response = await axios.post(`/tasks/${this.task.id}/reject`)
      if (response.data && response.data.data) {
        this.task.rejected_at = response.data.data.rejected_at
      }
    },

    formatDate (date) {
      return this.timezone
          ? moment.utc(date).tz(this.timezone).calendar()
          : moment.utc(date).local().calendar();
    },

    getDifference (startedAt, stoppedAt) {
      const minutes = moment(stoppedAt).diff(moment(startedAt), 'minutes');

      return minutes > 59 ? (minutes / 60).toFixed(2) + ' Hours' : Math.round(minutes) + ' Minutes';
    },
  },

  mounted() {
    this.playable = this.task.playable ?? [];
    if (this.task.users && this.task.users.length > 0) {
      this.task.users.forEach((u) => this.existingAssignees.set(u.id, u))
    }
  },
};
</script>

<style scoped lang="scss">
.task-title-input {
  resize: none;
  overflow: hidden;
  border-radius: 3px 0 0 3px;
  line-height: 1.3em;
  width: 200px;
}

.task-title {
  height: auto;
  line-height: 1.3em;
  width: 190px !important;
  padding: 6px 0 6px 12px;
}

.task-completed-at {
  font-size: 0.6rem;
  line-height: 1.2em;
  max-width: 60px;
  white-space: normal;
}

.task-details {
  font-size: 12px;
  margin-right: 4px;
}

.subtask-title {
  line-height: 1.2;
  color: #8997aa;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  letter-spacing: 0;
}
</style>
